/*
  Workflow Helper Functions

  Utility functions for building workflow props and initializing
  embedded workflow editors.
*/

import type { Dispatch } from '@reduxjs/toolkit';
import {
  initWorkflowPropsAndData,
  setFocusedNodeId,
  setActiveNodeEditorTab,
} from '~/dashboard/views/workflows/store/redux/slice';
import { setActiveEditor } from './slice';
import type { WorkflowPropsResponse } from 'flow/store/engine/types';
import type { FlowState } from '~/flow/types';

/**
 * Build workflow props object for workflow editor initialization.
 * Combines base props, workflow data, and matrix models into the format
 * expected by the workflow editor.
 */
export function buildWorkflowProps(
  baseWorkflowProps,
  workflowData,
  matrixModels,
) {
  return {
    aggregationConfig: {},
    allWorkflows: baseWorkflowProps.applications || [],
    importableAttributes: baseWorkflowProps.importableAttributes || {},
    lists: baseWorkflowProps.lists || [],
    customLists: baseWorkflowProps.customLists || [],
    matrixModels,
    outcomes: workflowData.outcomes || [],
    publishedAttributes: [],
    reasonCodeGroups: baseWorkflowProps.reasonCodeGroups || [],
    reasonCodes: [],
    recurringWorkflows: baseWorkflowProps.recurringWorkflows || {},
    services: baseWorkflowProps.services || {},
    tags: workflowData.tags || [],
    tagTypes: baseWorkflowProps.tagTypes || [],
    workflow: baseWorkflowProps.application,
    sandboxPersonas: baseWorkflowProps.sandboxPersonas || [],
    customers: [],
    categories: baseWorkflowProps.categories || [],
  };
}

/**
 * Initialize the workflow editor with cached data.
 * Dispatches all necessary actions to set up the workflow editor state.
 */
export function initializeWorkflowEditor(
  dispatch: Dispatch,
  workflowToken: string,
  cachedWorkflowData: FlowState,
  cachedWorkflowProps: WorkflowPropsResponse,
): void {
  dispatch(initWorkflowPropsAndData({
    workflowData: cachedWorkflowData,
    workflowProps: cachedWorkflowProps,
    featureFlags: {},
  }));

  dispatch(setFocusedNodeId(null));
  dispatch(setActiveNodeEditorTab(null));
  dispatch(setActiveEditor(workflowToken));
}
